import React, {useCallback, useState} from 'react';
import {priority} from '@priority-software/priority-proto-api/lib';
import {Icon, PriorityIcon, Text} from '@priority-software/priority-style-react';
import {IAlertConditionWithID} from '../../../../actions/proto';
import {operatorsWithValue} from '../../../../constants';
import {SingleValueControl} from './SingleValueControl';
import {RuleOperators} from '../../enum';
import {RangeValueControl} from './RangeValueControl';
import {SelectControl} from '../AutomationRuleSelect/SelectControl';
import {AutomationChoose} from '../../../../reducer/model';
import {GridContainer} from '../../../common/GridLayout/GridLayout';
import {AlertConditionFields} from '../../../../enums';
import {texts} from '../../../../texts';
import styles from './styles.module.scss';
import {createDataTestId} from '../../../../../../utils/dataTestId';

type IAlertChoose = priority.netitems.form.IAlertChoose;

type Props = {
    isOptional: boolean;
    removeCondition: (id: string) => void;
    condition: IAlertConditionWithID;
    onChangeCondition: (id: string, data: Partial<IAlertConditionWithID>) => void;
    ifOptions: Array<AutomationChoose>;
    ofOptions: Array<AutomationChoose>;
    isOptions: Array<AutomationChoose>;
    session: string;
    isBPM?: boolean;
};

export const AutomationRuleCondition = (props: Props) => {
    const {
        removeCondition,
        condition,
        onChangeCondition,
        ifOptions,
        ofOptions,
        isOptions,
        isOptional,
        isBPM
    } = props;
    const {
        id: conditionId,
        ConditionOperator,
        ConditionField,
        ConditionSecondValue,
        ConditionValue,
        CurrentValue
    } = condition;

    const handleAdditionalControl = useCallback(
        (value: string, name: string) => {
            onChangeCondition(conditionId, {[name]: value});
        },
        [onChangeCondition, conditionId]
    );

    const hasAdditionalFields = operatorsWithValue.includes(Number(ConditionOperator));

    const [zoom, setZoom] = useState<priority.netitems.form.ZoomType | null | undefined>(
        (ofOptions.find(item => item.value === ConditionField)?.data as IAlertChoose)?.Zoom
    );

    const handleSelect = useCallback(
        (option: AutomationChoose, name: string) => {
            const data = {[name]: option.value} as IAlertConditionWithID;

            if (name === AlertConditionFields.ConditionField) {
                const zoom = (option?.data as IAlertChoose)?.Zoom;
                setZoom(zoom);
                // set default condition operator value
                if (!ConditionOperator) {
                    data.ConditionOperator = isOptions[0]?.value as number;
                }

                // reset values related to previous selected field
                data.ConditionValue = undefined;
                data.ConditionSecondValue = undefined;
            }

            onChangeCondition(conditionId, data);
        },
        [onChangeCondition, conditionId, ConditionOperator, isOptions]
    );
    const renderTemplate = () => {
        return (
            <div className={styles.template} {...createDataTestId('condition-section')}>
                <GridContainer columnCount={3}>
                    <div className={styles.item} {...createDataTestId('if-block', false)}>
                        <Text tag="label" textColor="primary-700" skin="paragraph-2">
                            {isOptional ? texts.if_optional_label : texts.condition_if_label}
                        </Text>

                        <SelectControl
                            onChange={handleSelect}
                            options={ifOptions}
                            defaultValue={CurrentValue as number}
                            name={AlertConditionFields.CurrentValue}
                        />
                    </div>

                    <div className={styles.item} {...createDataTestId('of-block')}>
                        <Text tag="label" textColor="primary-700" skin="paragraph-2">
                            {texts.condition_of_label}
                        </Text>

                        <SelectControl
                            onChange={handleSelect}
                            options={ofOptions}
                            defaultValue={ConditionField as number}
                            name={AlertConditionFields.ConditionField}
                        />
                    </div>

                    <div className={styles.item} {...createDataTestId('condition-value-block')}>
                        <SelectControl
                            onChange={handleSelect}
                            options={isOptions}
                            defaultValue={ConditionOperator as number}
                            isDisabled={!ConditionField}
                            name={AlertConditionFields.ConditionOperator}
                        />
                    </div>
                </GridContainer>

                {hasAdditionalFields ? (
                    ConditionOperator === RuleOperators.fallsBetween ? (
                        <RangeValueControl
                            zoom={zoom}
                            key={`${ConditionField}`}
                            relatedField={ConditionField as number}
                            value={ConditionValue}
                            secondValue={ConditionSecondValue}
                            onChange={handleAdditionalControl}
                            name={AlertConditionFields.ConditionValue}
                            secondName={AlertConditionFields.ConditionSecondValue}
                            isBPM={isBPM}
                        />
                    ) : (
                        <GridContainer columnCount={1}>
                            <SingleValueControl
                                key={`${ConditionField}`}
                                relatedField={ConditionField as number}
                                zoom={zoom}
                                onChange={handleAdditionalControl}
                                value={ConditionValue}
                                name={AlertConditionFields.ConditionValue}
                                placeholder={texts.condition_value_label}
                                isBPM={isBPM}
                            />
                        </GridContainer>
                    )
                ) : null}
            </div>
        );
    };

    return (
        <div className={styles.component}>
            <Icon
                icon={PriorityIcon.closeSmall}
                iconColor="primary-700"
                componentClasses={styles.close}
                onClick={() => removeCondition(condition.id)}
            />
            {renderTemplate()}
        </div>
    );
};
